import { FunctionComponent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";

import { MessageSeverity } from "primereact/api";
import { classNames } from "primereact/utils";

import { faTrash } from "@fortawesome/pro-solid-svg-icons";

import { ActionButton } from "../../../../components/Shared";
import { TooltipWrapper } from "../../../../components/Shared/Components/TooltipWrapper/TooltipWrapper";
import { confirmAction } from "../../../../components/Shared/Functions/ConfirmActionDialogFunction";
import { translationKeys } from "../../../../models/app-translation-keys";
import { IChatThreadExtendedModel } from "../../../../models/interfaces";
import { useThreadService } from "../../../../services/ServiceHooks/threadHook";
import { useStore } from "../../../../stores/StoreContext";

import "./ThreadItem.scss";

interface IThreadItemProps {
  thread: IChatThreadExtendedModel;
  isActive: boolean;
}

const RawThreadItem: FunctionComponent<IThreadItemProps> = (props: IThreadItemProps) => {
  const { t } = useTranslation();
  const { toastStore } = useStore();
  const threadService = useThreadService();
  const { visibleToUser, threadSummary } = props.thread;

  const threadSummaryRef = useRef(null);
  const deleteButtonRef = useRef<HTMLButtonElement>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const selectActiveThread = async (thread: IChatThreadExtendedModel) => {
    await threadService.openThread(thread);
  };

  const handleDeleteClick: React.MouseEventHandler<HTMLButtonElement> = async event => {
    event.stopPropagation();

    const tk = translationKeys.sidebarThreads.deleteThread;
    const confirmed = await confirmAction(t, {
      header: t(tk.confirmTitle),
      message: t(tk.confirmMessage, { title: props.thread.threadSummary ?? "" }),
      acceptLabel: t(tk.deleteLabel),
      rejectLabel: t(tk.cancelLabel),
      dismissableMask: true, // close dialog when clicking outside
    });

    if (!confirmed) {
      return;
    }

    setIsDeleting(true);
    try {
      await threadService.deleteThreadById(props.thread.id!);

      // Ensure keyboard users keep context in the sidebar after an item is deleted.
      window.requestAnimationFrame(() => {
        const firstThreadItem = document.querySelector(".thread-list .thread-item") as HTMLElement | null;
        if (firstThreadItem) {
          firstThreadItem.focus();
          return;
        }

        const threadList = document.querySelector(".thread-list") as HTMLElement | null;
        threadList?.focus();
      });

      toastStore.addToast({
        severity: MessageSeverity.SUCCESS,
        summary: { translationKey: tk.successToast },
        detail: { message: "" },
      });
    } catch {
      toastStore.addToast({
        severity: MessageSeverity.ERROR,
        summary: { translationKey: tk.errorToast },
        detail: { message: "" },
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div
      className={classNames("thread-item sidebarThreadSummaryTooltip p-ripple", {
        "is-active": props.isActive,
      })}
      onClick={() => selectActiveThread(props.thread)}
      tabIndex={0}
      onKeyDown={event => event.key === "Enter" && selectActiveThread(props.thread)}
      role="button"
    >
      <TooltipWrapper targetRef={threadSummaryRef} content={threadSummary} showDelay={300} />

      <TooltipWrapper
        targetRef={deleteButtonRef}
        content={t(translationKeys.sidebarThreads.deleteThread.tooltip)}
        showDelay={300}
      />

      <ActionButton
        id="thread-delete-button"
        icon={faTrash}
        customClasses="thread-item-delete-button"
        active={props.isActive}
        hideBackground={true}
        buttonDisabled={isDeleting}
        onClick={handleDeleteClick}
        tooltip={t(translationKeys.sidebarThreads.deleteThread.ariaLabel)}
        tooltipPosition="right"
      />

      <div className="thread-item-summary" ref={threadSummaryRef}>
        <div
          className={classNames("thread-item-label", {
            deleted: !visibleToUser,
          })}
        >
          {threadSummary?.length ? (
            threadSummary
          ) : (
            <span className="no-summary-available">{t(translationKeys.thread.error.noSummaryAvailable)} </span>
          )}
        </div>
      </div>
    </div>
  );
};

export const ThreadItem = observer(RawThreadItem);
